import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router";
import { Field, Formik } from 'formik';
import moment from 'moment';
import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import { KeyboardDatePicker } from "formik-material-ui-pickers";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import { TextField } from "formik-material-ui";
import { Typography } from "@material-ui/core";
import Box from "@material-ui/core/Box";
import { makeStyles } from "@material-ui/core/styles";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import Alert from "@material-ui/lab/Alert";
import { get } from 'lodash';

import PurchaseInfo from "../../Common/CartInfo";
import {
    getActivePromoCode,
    getAllParkers,
    getFacilityId,
    getParkerData,
    getParkerInitValues,
    getParkingRates,
    getProgress,
    getPromoCodeOnly,
    setParkingSpot
} from "../../../../Store/purchase.slice";
import {
    fetchBillingProfiles,
    isFetchBillingProfilesLoaded,
    isFetchBillingProfilesLoading,
} from "../../../../Store/billingProfiles.slice";
import { fetchDisplayPrices } from '../../../../Store/rates.slice';

const useStyles = makeStyles(theme => ({
    rateSelect: {
        maxWidth: 600,
        marginTop: theme.spacing(1)
    },
    rateError: { width: '100%' },
    rateOption: {
        margin: 0,
        padding: 0,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "flex-start",
        borderTop: '1px solid black',
        borderLeft: '1px solid black',
        borderRight: '1px solid black',
        '&:first-child': {
            borderTopLeftRadius: '3px',
            borderTopRightRadius: '3px'
        },
        '&:last-child': {
            borderBottom: '1px solid black',
            borderBottomLeftRadius: '3px',
            borderBottomRightRadius: '3px'
        },
        '&:hover, &.Mui-selected': {
            border: `1px solid ${theme.palette.primary.main}`,
            '& + $rateOption': { borderTop: 0 },
            '& > $rateInfoCost': {
                backgroundColor: theme.palette.primary.main,
                '& > .MuiTypography-colorTextPrimary': { color: 'white' },
            }
        }
    },
    rateInfoCost: {
        width: '20%',
        minWidth: 'fit-content',
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(1),
    },
    rateInfoText: {
        margin: 0,
        width: '80%',
        overflowWrap: "break-word",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    }
}))

export function RatesPage() {
    const history = useHistory();
    const { parkerId } = useParams();
    const dispatch = useDispatch();
    const facilityId = useSelector(getFacilityId);
    const promoCodeOnly = useSelector(getPromoCodeOnly);
    const progress = useSelector(getProgress);
    const parkers = useSelector(getAllParkers);
    const parkerInit = useSelector(getParkerInitValues);
    const parkerData = useMemo(() => getParkerData(parkers, parkerId), [parkerId, parkers])
    const rateId = parkerData.rateId || parkerInit.rateId;
    const activePromoCode = useSelector(getActivePromoCode);
    const promoCode = parkerData.promoCode || parkerInit.promoCode;
    const [promoApplied, setPromoApplied] = useState(false);
    const rates = useSelector(getParkingRates);
    const loaded = useSelector(isFetchBillingProfilesLoaded());
    const loading = useSelector(isFetchBillingProfilesLoading());
    const [displayPrices, setDisplayPrices] = useState([]);
    const [displayPriceLoading, setDisplayPriceLoading] = useState(false);

    const today = moment().startOf('day');
    const [startDate, setStartDate] = useState(parkerData.startDate ? moment(parkerData.startDate) : today)
    const init = { rateId, promoCode, startDate: startDate };
    const isValid = useCallback(async (values) => {
        const errors = {};
        if (!values.rateId) errors.rateId = 'You must pick a rate to continue.';
        if (!values.startDate) errors.startDate = 'Start date is required.';
        const selectedRate = rates.find(rate => rate.id === values.rateId);
        const monthsToSellInAdvance = get(selectedRate, 'months_to_sell_in_advance', 12);
        const maxSellDate = moment(today).add(monthsToSellInAdvance, 'months');
        if (values.startDate?.isAfter(maxSellDate, 'month')) errors.startDate = 'Start date is too far in advance';
        return errors;
    }, []);

    const submit = useCallback(async ({ rateId, promoCode, startDate }) => {
        const parker = {
            id: parkerId, rateId, promoCode: promoApplied ? promoCode : null, startDate: startDate.format('YYYY-MM-DD')
        };
        await dispatch(setParkingSpot(parker));
        history.push(`/parker/${parkerId}`)
    }, [dispatch, history, parkerId, promoApplied])

    const shouldDispatch = !loading && !loaded && !rates.length && !displayPriceLoading;
    const classes = useStyles();

    const getRatesList = useCallback(async (promoCode, date) => {
        setDisplayPriceLoading(true);
        const { payload: bpPayload } = await dispatch(fetchBillingProfiles({ facilityId, promoCode, startDate: date ? date.toISOString() : startDate.toISOString() }));
        const rateIds = Object.keys(bpPayload.rates ?? {});
        if (rateIds?.length) {
            const { payload: pricesPayload } = await dispatch(fetchDisplayPrices({ rateIds }));    
            setDisplayPrices(pricesPayload);
        }

        setDisplayPriceLoading(false);
    }, [dispatch, facilityId, startDate]);

    const getDisplayPrices = useCallback(async (rateIds) => {
        setDisplayPriceLoading(true);

        const { payload } = await dispatch(fetchDisplayPrices({ rateIds }));    
        setDisplayPrices(payload);

        setDisplayPriceLoading(false);
    }, [dispatch]);

    useEffect(() => {
        if (shouldDispatch) {
            (async () => await getRatesList(promoCode))();
        } else if (rates.length && !displayPrices.length && !displayPriceLoading) { // Needed for when "Back" button is used
            (async () => await getDisplayPrices(rates.map(({ id }) => id)))();
        }
    }, [displayPriceLoading, displayPrices, getDisplayPrices, getRatesList, promoCode, rates, shouldDispatch]);

    const isLoading = loading || displayPriceLoading;

    const handleStartDateChange = useCallback((date, setFieldValue) => {
        setStartDate(date);
        setFieldValue('startDate', date)
        setFieldValue('rateId', '');
        (async () => await getRatesList(promoCode, date))();
    }, [getRatesList, promoCode]);

    const ParkchirpKeyboardDatePicker = useCallback((props) => {
        const setFieldValue = props.form.setFieldValue;
        return (
            <KeyboardDatePicker
                value={startDate}
                onChange={(value) => handleStartDateChange(value, setFieldValue)}
                {...props}
                data-cy="start-date-picker"
            />
        );
    }, [startDate, handleStartDateChange]);


    return <Formik initialValues={init} validate={isValid} onSubmit={submit}>
        {({ errors, values, dirty, setFieldValue, submitForm }) => <Grid container>
            <Grid item xs={12}>
                <LinearProgress variant="determinate" value={progress} />
            </Grid>
            <Grid item xs={12} md={7}>
                <Box m={3}>
                    <Box py={1}><Typography variant="h5">Select Your Start Date</Typography></Box>
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                        <Field component={ParkchirpKeyboardDatePicker} name="startDate" variant="inline"
                            minDateMessage='Start date cannot be in the past.' minDate={today}
                            autoOk format='MM-DD-YYYY' label='Start Date' />
                    </MuiPickersUtilsProvider>
                </Box>
                <Box m={3}>
                    <Box py={1}><Typography variant='h5'>Select Your Parking Type</Typography></Box>
                    {promoCodeOnly === 'true' ? (
                        <Typography variant="body2">This parking facility only offers promo code rates.</Typography>
                    ) : null}
                    <List className={classes.rateSelect}>
                        {isLoading && <ListItem key='loading'>Loading...</ListItem>}
                        {!isLoading && rates.map(({ id, name, description }) => (<ListItem
                            key={id} className={classes.rateOption} selected={values.rateId === id}
                            onClick={() => setFieldValue('rateId', id)} button data-cy="parking-type-rate-button">
                            <ListItemIcon className={classes.rateInfoCost}>
                                <Typography
                                    variant="h6"
                                    color="textPrimary"
                                    data-cy="parking-type-rate-value-text"
                                >
                                    ${displayPrices.find(({ rateId }) => rateId === id)?.displayPrice}
                                </Typography>
                            </ListItemIcon>
                            <ListItemText className={classes.rateInfoText} primary={name} secondary={description}
                                primaryTypographyProps={{ variant: 'body1' }}
                                secondaryTypographyProps={{ color: 'textSecondary' }}
                                data-cy="parking-type-rate-name-text" />
                        </ListItem>))}
                        {errors.rateId ? <ListItem key='error' className={classes.rateOption}>
                            <Alert className={classes.rateError} severity='error' width={1}>{errors.rateId}</Alert>
                        </ListItem> : ''}
                    </List>
                </Box>
                <Box m={3}>
                    <Box py={1}><Typography variant='h5'>Enter Promo or Group Code</Typography></Box>
                    <Field component={TextField} name="promoCode" label="Promo Code" placeholder="EXAMPLE-PROMO" data-cy="promo-code-input" />
                    <Button variant="contained" color={activePromoCode === values.promoCode ? 'default' : 'primary'}
                        onClick={async () => {
                            setFieldValue('rateId', '');
                            const code = activePromoCode === values.promoCode ? '' : values.promoCode;
                            await getRatesList(code);
                            setPromoApplied(true);
                            if (activePromoCode === values.promoCode) setFieldValue('promoCode', code);
                        }} disabled={!!loading || !values['promoCode']} disableElevation data-cy="promo-code-submit-button">
                        {activePromoCode === values.promoCode ? 'Clear' : 'Apply'}
                    </Button></Box>
            </Grid>
            <Grid item xs={12} md={5}><Box m={3}>
                {rates.length > 0 && values.rateId && <PurchaseInfo
                    confirmOnRedirect={!!dirty}
                    currentParkerId={parkerId}
                    selectedRateId={values.rateId}
                    rateStartDate={values.startDate?.format('YYYY-MM-DD')}
                />}
            </Box></Grid>
            <Grid container item xs={12} justify="flex-end">
                <Box m={3}><Button size="large" variant="contained" color="primary"
                    onClick={submitForm} disabled={!!errors?.startDate} data-cy="checkout-submit-button">Next</Button></Box>
            </Grid>
        </Grid>}
    </Formik>
}

export default React.memo(RatesPage);
